import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaIdCardAlt,
  FaBriefcase,
  FaPhoneAlt,
  FaShieldAlt,
  FaFileAlt,
  FaWallet,
  FaRegClock,
  FaGraduationCap,
  FaUsers,
  FaCalendarAlt,
  FaFolderOpen,
  FaEye,
  FaHistory,
  FaInfoCircle,
} from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import { API_BASE_URL, apiRequest } from '../utils/api';
import { resolveFileFromCandidates } from '../utils/fileUrl';
import { buildServiceHistoryTimeline } from '../utils/serviceHistory';
import './EmployeeView.css';
import { getActiveCompanyId } from '../utils/companyContext';
import { getModulePermissions, hasModulePermission, isDirectEmployeeAdmin } from '../utils/permissions';
import useDocumentTypes from '../hooks/useDocumentTypes';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const buildProtectedHeaders = () => {
  const token = localStorage.getItem('authToken');
  const companyId = getActiveCompanyId();

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(companyId ? { 'x-company-id': companyId } : {}),
  };
};

const getDisplayValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string' && value.trim() === '') {
    return '—';
  }

  return value;
};

const formatEnumValue = (value) => {
  const displayValue = getDisplayValue(value);
  if (displayValue === '—') {
    return displayValue;
  }

  return String(displayValue)
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrencyValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  return `₹${numeric.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`;
};

const formatTime = (value) => {
  if (!value) {
    return '—';
  }

  const match = /^([0-2]?\d):([0-5]\d)(?::([0-5]\d))?$/.exec(String(value));
  if (!match) {
    return value;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatShiftDisplay = (shift) => {
  if (!shift) {
    return '—';
  }

  const start = formatTime(shift.timeFrom ?? shift.shiftInTime);
  const end = formatTime(shift.timeTo ?? shift.shiftOutTime);

  const parts = [];
  if (start !== '—' || end !== '—') {
    parts.push(`${start !== '—' ? start : '—'} - ${end !== '—' ? end : '—'}`);
  }

  if (shift.shiftName) {
    parts.push(`(${shift.shiftName})`);
  }

  return parts.length > 0 ? parts.join(' ') : '—';
};

const getBooleanDisplay = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  return value ? 'Yes' : 'No';
};

const resolveDocumentUrl = (...paths) => resolveFileFromCandidates(...paths);
const appendVersionParam = (url, version) => {
  if (!url) return '';
  if (!version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(version))}`;
};

const extractFileNameFromPath = (path) => {
  if (!path) {
    return null;
  }

  const sanitized = String(path).split('?')[0];
  const segments = sanitized.split(/[/\\]+/).filter(Boolean);
  if (!segments.length) {
    return sanitized || null;
  }

  return segments[segments.length - 1];
};

const renderAddressValue = (value) => {
  const displayValue = getDisplayValue(value);
  if (displayValue === '—') {
    return displayValue;
  }

  const lines = String(displayValue)
    .split(/\r?\n/) // Handle multi-line addresses gracefully
    .map((line) => line.trim())
    .filter((line) => line !== '');

  if (lines.length === 0) {
    return '—';
  }

  return lines.map((line, index, array) => (
      <React.Fragment key={`address-line-${index}`}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
};

const resolveUserName = (user) => {
  if (!user) {
    return null;
  }

  if (typeof user === 'string') {
    return user;
  }

  if (typeof user === 'object') {
    return user.name || user.username || user.email || user.id || null;
  }

  return null;
};

const buildUserTimestampValue = (user, timestamp) => {
  const name = resolveUserName(user);
  const timeLabel = formatDateTime(timestamp);

  if (name && timeLabel && timeLabel !== '—') {
    return `${name} on ${timeLabel}`;
  }

  if (name) {
    return name;
  }

  if (timeLabel && timeLabel !== '—') {
    return timeLabel;
  }

  return '—';
};

const EmployeeView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formUPreviewLoading, setFormUPreviewLoading] = useState(false);
  const [formUError, setFormUError] = useState(null);
  const [form11Loading, setForm11Loading] = useState(false);
  const [form11Error, setForm11Error] = useState(null);
  const [gsmPreviewLoading, setGsmPreviewLoading] = useState(false);
  const [gsmError, setGsmError] = useState(null);
  const [documentPreview, setDocumentPreview] = useState({
    visible: false,
    url: null,
    filename: null,
    title: '',
  });
  const [loginDetails, setLoginDetails] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginInfoMessage, setLoginInfoMessage] = useState('');
  const [loginSuccess, setLoginSuccess] = useState('');
  const [isEditingLogin, setIsEditingLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '', status: 'active' });
  const [loginSaving, setLoginSaving] = useState(false);
  const [qualificationLookup, setQualificationLookup] = useState({});
  const [qualificationLookupError, setQualificationLookupError] = useState('');
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [documentUploadForm, setDocumentUploadForm] = useState({ documentTypeId: '', file: null });
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [documentUploadMessage, setDocumentUploadMessage] = useState('');
  const [documentUploadError, setDocumentUploadError] = useState('');
  const employeeViewPermissions = useMemo(() => getModulePermissions('employee.view'), []);
  const employeeCreateEditPermissions = useMemo(() => getModulePermissions('employee.create_edit'), []);
  const mobileLoginPermissions = useMemo(() => getModulePermissions('employee.mobile_logins'), []);
  const employeeFilePermissions = useMemo(() => getModulePermissions('employee.files'), []);
  const { documentTypes, isLoading: isLoadingDocumentTypes, error: documentTypeError } = useDocumentTypes({ activeOnly: true });
  const canExportEmployee = employeeViewPermissions.export;
  const canEditEmployee = employeeCreateEditPermissions.edit;
  const canManageMobileLogin = mobileLoginPermissions.edit || mobileLoginPermissions.create;
  const canAddEmployeeDocument = isDirectEmployeeAdmin() && (employeeFilePermissions.create || hasModulePermission('employee.files', 'create'));

  const loadEmployeeDetails = useCallback(
    async ({ showLoader = true } = {}) => {
      const employeeId = Number(id);
      if (!employeeId || Number.isNaN(employeeId)) {
        setEmployee(null);
        setError('Invalid employee id provided.');
        setLoading(false);
        return null;
      }

      try {
        if (showLoader) {
          setLoading(true);
        }
        setError(null);
        const response = await apiRequest(`/api/admin/employees/${employeeId}`, {}, navigate);
        setEmployee(response.data ?? null);
        return response.data ?? null;
      } catch (err) {
        setEmployee(null);
        setError(err.message || 'Failed to load employee details.');
        return null;
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [id, navigate],
  );

  useEffect(() => {
    setFormUPreviewLoading(false);
    setFormUError(null);
    setForm11Loading(false);
    setForm11Error(null);
    setGsmPreviewLoading(false);
    setGsmError(null);

    loadEmployeeDetails();
  }, [loadEmployeeDetails]);

  useEffect(() => {
    let isMounted = true;

    const loadQualificationLookup = async () => {
      try {
        setQualificationLookupError('');
        const response = await apiRequest('/api/public/education-qualifications', {
          skipAuthRedirect: true,
        });
        if (!isMounted) {
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        const lookup = list.reduce((accumulator, option) => {
          const code = (option?.code || '').toUpperCase();
          if (code) {
            accumulator[code] = (option?.title || option?.code || '').toUpperCase();
          }
          return accumulator;
        }, {});
        setQualificationLookup(lookup);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setQualificationLookup({});
        setQualificationLookupError(
          err.message || 'Failed to load education qualifications.',
        );
      }
    };

    loadQualificationLookup();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadLoginDetails = useCallback(
    async (employeeId) => {
      if (!employeeId) {
        setLoginDetails(null);
        setLoginInfoMessage('');
        return;
      }

      try {
        setLoginLoading(true);
        setLoginError('');
        setLoginSuccess('');
        const response = await apiRequest(`/api/admin/employees/${employeeId}/login`, {}, navigate);
        setLoginDetails(response.data ?? null);
        const infoMessage =
          response.message ||
          (response.data?.temporaryPassword
            ? 'Share this password with the employee and remind them to change it after login.'
            : '');
        setLoginInfoMessage(infoMessage);
        setLoginForm({
          username: response.data?.username ?? '',
          password: '',
          status: response.data?.status ?? 'active',
        });
      } catch (err) {
        setLoginDetails(null);
        setLoginInfoMessage('');
        setLoginError(err.message || 'Failed to load login details.');
      } finally {
        setLoginLoading(false);
      }
    },
    [navigate],
  );

  const safeEmployeeIdentifier = useMemo(() => {
    if (!employee?.id) {
      return 'employee';
    }

    const identifier = employee.permId || employee.tempId || employee.showId || employee.id;
    const normalized = String(identifier || 'employee')
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return normalized || `employee-${employee.id}`;
  }, [employee?.id, employee?.permId, employee?.showId, employee?.tempId]);

  const fetchEmployeePdfBlob = useCallback(
    async ({ endpoint, defaultError }) => {
      if (!employee?.id) {
        throw new Error('Employee details are not available.');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/employees/${employee.id}/${endpoint}`, {
        headers: buildProtectedHeaders(),
      });

      if (!response.ok) {
        let message = defaultError || 'Failed to prepare the PDF.';
        try {
          const result = await response.json();
          if (result?.message) {
            message = result.message;
          }
        } catch (parseError) {
          // Ignore JSON parse failures and use the default error message.
        }
        throw new Error(message);
      }

      return response.blob();
    },
    [employee?.id],
  );

  const showDocumentPreview = useCallback(({ url, filename, title }) => {
    setDocumentPreview((prev) => {
      if (prev.url) {
        window.URL.revokeObjectURL(prev.url);
      }
      return {
        visible: true,
        url,
        filename: filename || 'employee-document.pdf',
        title: title || 'Document Preview',
      };
    });
  }, []);

  const handleFormUPreview = useCallback(async () => {
    if (!employee?.id || formUPreviewLoading) {
      return;
    }

    try {
      setFormUPreviewLoading(true);
      setFormUError(null);

      const blob = await fetchEmployeePdfBlob({
        endpoint: 'form-u',
        defaultError: 'Failed to generate Form U.',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      showDocumentPreview({
        url: blobUrl,
        filename: `form-u-${safeEmployeeIdentifier}.pdf`,
        title: 'Form U Preview',
      });
    } catch (err) {
      setFormUError(err.message || 'Failed to generate Form U.');
    } finally {
      setFormUPreviewLoading(false);
    }
  }, [employee?.id, fetchEmployeePdfBlob, formUPreviewLoading, safeEmployeeIdentifier, showDocumentPreview]);

  const handleGsmPdfPreview = useCallback(async () => {
    if (!employee?.id || gsmPreviewLoading) {
      return;
    }

    try {
      setGsmPreviewLoading(true);
      setGsmError(null);

      const blob = await fetchEmployeePdfBlob({
        endpoint: 'gsm-pdf',
        defaultError: 'Failed to generate GSM PDF.',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      showDocumentPreview({
        url: blobUrl,
        filename: `gsm-employee-${safeEmployeeIdentifier}.pdf`,
        title: 'Resume Preview',
      });
    } catch (err) {
      setGsmError(err.message || 'Failed to generate GSM PDF.');
    } finally {
      setGsmPreviewLoading(false);
    }
  }, [employee?.id, fetchEmployeePdfBlob, gsmPreviewLoading, safeEmployeeIdentifier, showDocumentPreview]);

  const handleForm11Preview = useCallback(async () => {
    if (!employee?.id || form11Loading) {
      return;
    }

    try {
      setForm11Loading(true);
      setForm11Error(null);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/employees/${employee.id}/form-11`,
        {
          headers: buildProtectedHeaders(),
        },
      );

      if (!response.ok) {
        let message = 'Failed to generate Form 11.';
        try {
          const result = await response.json();
          if (result?.message) {
            message = result.message;
          }
        } catch (parseError) {
          // Ignore JSON parse failures and use default message.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      showDocumentPreview({
        url: blobUrl,
        filename: `form-11-${safeEmployeeIdentifier}.pdf`,
        title: 'Form 11 Preview',
      });
    } catch (err) {
      setForm11Error(err.message || 'Failed to generate Form 11.');
    } finally {
      setForm11Loading(false);
    }
  }, [employee, form11Loading, safeEmployeeIdentifier, showDocumentPreview]);

  const handleCloseDocumentPreview = useCallback(() => {
    setDocumentPreview((prev) => {
      if (prev.url) {
        window.URL.revokeObjectURL(prev.url);
      }
      return {
        visible: false,
        url: null,
        filename: null,
        title: '',
      };
    });
  }, []);

  useEffect(() => {
    return () => {
      if (documentPreview.url) {
        window.URL.revokeObjectURL(documentPreview.url);
      }
    };
  }, [documentPreview.url]);

  useEffect(() => {
    if (!documentPreview.visible) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseDocumentPreview();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [documentPreview.visible, handleCloseDocumentPreview]);

  useEffect(() => {
    if (!documentPreview.visible || typeof document === 'undefined') {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [documentPreview.visible]);

  useEffect(() => {
    if (!employee?.id) {
      setLoginDetails(null);
      setLoginInfoMessage('');
      return;
    }

    loadLoginDetails(employee.id);
  }, [employee?.id, loadLoginDetails]);

  useEffect(() => {
    if (!loginSuccess) {
      return undefined;
    }

    const timer = window.setTimeout(() => setLoginSuccess(''), 5000);
    return () => window.clearTimeout(timer);
  }, [loginSuccess]);

  const handleOpenAddDocument = () => {
    setDocumentUploadForm({ documentTypeId: '', file: null });
    setDocumentUploadError('');
    setDocumentUploadMessage('');
    setIsAddDocumentOpen(true);
  };

  const handleCloseAddDocument = () => {
    if (documentUploadLoading) {
      return;
    }
    setIsAddDocumentOpen(false);
    setDocumentUploadForm({ documentTypeId: '', file: null });
    setDocumentUploadError('');
  };

  const handleAddDocumentSubmit = async (event) => {
    event.preventDefault();
    setDocumentUploadError('');
    setDocumentUploadMessage('');

    if (!documentUploadForm.documentTypeId) {
      setDocumentUploadError('Please select a document type.');
      return;
    }

    if (!documentUploadForm.file) {
      setDocumentUploadError('Please choose a document file.');
      return;
    }

    try {
      setDocumentUploadLoading(true);
      const formData = new FormData();
      formData.append('documentTypeId', documentUploadForm.documentTypeId);
      formData.append('file', documentUploadForm.file);

      const response = await apiRequest(`/api/admin/employees/${id}/documents`, {
        method: 'POST',
        body: formData,
      }, navigate);

      await loadEmployeeDetails({ showLoader: false });
      setDocumentUploadMessage(response.message || 'Employee document added successfully');
      setDocumentUploadForm({ documentTypeId: '', file: null });
      setIsAddDocumentOpen(false);
    } catch (err) {
      setDocumentUploadError(err.message || 'Failed to add employee document.');
    } finally {
      setDocumentUploadLoading(false);
    }
  };

  const handleOpenLoginEditor = () => {
    setLoginError('');
    setLoginSuccess('');
    setIsEditingLogin(true);
    setLoginForm({
      username: loginDetails?.username ?? employee?.showId ?? '',
      password: '',
      status: loginDetails?.status ?? 'active',
    });
  };

  const handleCancelLoginEdit = () => {
    setLoginError('');
    setIsEditingLogin(false);
    setLoginForm({
      username: loginDetails?.username ?? employee?.showId ?? '',
      password: '',
      status: loginDetails?.status ?? 'active',
    });
  };

  const handleLoginInputChange = (field) => (event) => {
    const { value } = event.target;
    setLoginForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!employee?.id || loginSaving) {
      return;
    }

    const usernameValue = (loginForm.username || '').trim();
    if (!usernameValue) {
      setLoginError('Username is required.');
      return;
    }

    const normalizedStatus = (loginForm.status || 'active').toLowerCase() === 'disabled' ? 'disabled' : 'active';
    const passwordValue = (loginForm.password || '').trim();

    const payload = {
      username: usernameValue,
      status: normalizedStatus,
    };

    if (passwordValue) {
      payload.password = passwordValue;
    }

    try {
      setLoginSaving(true);
      setLoginError('');
      const response = await apiRequest(
        `/api/admin/employees/${employee.id}/login`,
        {
          method: 'PUT',
          body: payload,
        },
        navigate,
      );

      setLoginDetails(response.data ?? null);
      const infoMessage =
        response.message ||
        (response.data?.temporaryPassword
          ? 'Share this password with the employee and remind them to change it after login.'
          : '');
      setLoginInfoMessage(infoMessage);
      setLoginSuccess(response.message ?? 'Login details saved successfully.');
      setIsEditingLogin(false);
      setLoginForm({
        username: response.data?.username ?? usernameValue,
        password: '',
        status: response.data?.status ?? normalizedStatus,
      });
    } catch (err) {
      setLoginError(err.message || 'Failed to save login details.');
    } finally {
      setLoginSaving(false);
    }
  };

  const statusLabel = useMemo(() => {
    if (!employee?.status) {
      return { label: 'Inactive', tone: 'inactive' };
    }

    const normalized = String(employee.status).toLowerCase();
    if (normalized === 'active') {
      return { label: 'Active', tone: 'active' };
    }

    return { label: employee.status, tone: 'inactive' };
  }, [employee?.status]);

  const designationValue = useMemo(
    () => getDisplayValue(employee?.designation),
    [employee?.designation],
  );

  const serviceHistoryTimeline = useMemo(
    () =>
      buildServiceHistoryTimeline(employee?.serviceHistory ?? [], {
        defaultStartDate:
          employee?.dateOfJoin ??
          employee?.professional?.fromDate ??
          employee?.createdAt ??
          null,
        defaultOutlet: employee?.outlet ?? null,
      }),
    [
      employee?.createdAt,
      employee?.dateOfJoin,
      employee?.outlet,
      employee?.professional?.fromDate,
      employee?.serviceHistory,
    ],
  );

  const joiningDateRaw = useMemo(
    () =>
      employee?.dateOfJoin ??
      serviceHistoryTimeline.periods?.[0]?.startDate ??
      employee?.professional?.fromDate ??
      employee?.createdAt ??
      null,
    [
      employee?.createdAt,
      employee?.dateOfJoin,
      employee?.professional?.fromDate,
      serviceHistoryTimeline.periods,
    ],
  );

  const joiningDateValue = useMemo(() => formatDate(joiningDateRaw), [joiningDateRaw]);

  const serviceDurationValue = useMemo(
    () => serviceHistoryTimeline.totalDurationLabel || '—',
    [serviceHistoryTimeline.totalDurationLabel],
  );

  const referredByValue = useMemo(() => {
    const referrer = employee?.referredEmployee;
    if (!referrer) {
      return '—';
    }

    const parts = [];
    const name = getDisplayValue(referrer.name);
    if (name !== '—') {
      parts.push(name);
    }

    const identifier =
      referrer.permId || referrer.tempId || referrer.showId || referrer.id || null;
    if (identifier) {
      parts.push(`(${identifier})`);
    }

    if (referrer.phone) {
      parts.push(`• ${referrer.phone}`);
    }

    return parts.length > 0 ? parts.join(' ') : '—';
  }, [employee?.referredEmployee]);

  const summaryFields = useMemo(() => {
    const fields = [
      { label: 'Outlet', value: getDisplayValue(employee?.outlet?.name) },
      { label: 'Temporary ID', value: getDisplayValue(employee?.tempId) },
      { label: 'Permanent ID', value: getDisplayValue(employee?.permId) },
      { label: 'Date of Joining', value: joiningDateValue },
      { label: 'Salary', value: formatCurrencyValue(employee?.salary) },
      { label: 'Designation', value: designationValue },
      { label: 'Insurance Number', value: getDisplayValue(employee?.pf?.esiNumber) },
      { label: 'Insurer Name', value: getDisplayValue(employee?.pf?.insurerName) },
      { label: 'Referred By', value: referredByValue },
      { label: 'Service Duration', value: serviceDurationValue },
    ];

    if ((employee?.status || '').toLowerCase() === 'inactive') {
      fields.push(
        { label: 'Relieving Date', value: formatDate(employee?.relievingDate) },
        { label: 'Relieving Reason', value: formatEnumValue(employee?.inactiveReason) },
      );
    }

    return fields;
  }, [
    designationValue,
    employee?.inactiveReason,
    employee?.outlet?.name,
    employee?.permId,
    employee?.pf?.esiNumber,
    employee?.pf?.insurerName,
    employee?.relievingDate,
    employee?.salary,
    employee?.status,
    employee?.tempId,
    joiningDateValue,
    referredByValue,
    serviceDurationValue,
  ]);

  const profilePhoto = useMemo(() => {
    if (!Array.isArray(employee?.documents)) {
      return null;
    }

    const photoDocument = employee.documents.find((document) => {
      const type = (document?.documentType || '').toLowerCase();
      return type.includes('photo');
    });

    if (!photoDocument) {
      return null;
    }

    const resolvedName = (employee?.name || '').trim() || 'Employee';
    const resolvedPhotoUrl = resolveDocumentUrl(photoDocument.fileUrl, photoDocument.filePath, photoDocument.storedFileKey);
    if (!resolvedPhotoUrl) {
      return null;
    }
    const version = employee?.updatedAt || photoDocument?.updatedAt || photoDocument?.id || Date.now();

    return {
      url: appendVersionParam(resolvedPhotoUrl, version),
      fileName: photoDocument.fileName ?? '',
      altText: photoDocument.fileName
        ? photoDocument.fileName
        : `${resolvedName} photo`,
    };
  }, [employee?.documents, employee?.name, employee?.updatedAt]);

  const profileInitial = useMemo(() => {
    const name = (employee?.name || '').trim();
    return name ? name.charAt(0).toUpperCase() : '';
  }, [employee?.name]);

  const personalFields = useMemo(() => {
    const maritalStatus = (employee?.personal?.maritalStatus || '').toLowerCase();
    const gender = (employee?.personal?.gender || '').toLowerCase();
    const shouldShowSpouse = gender === 'female' && maritalStatus === 'married';
    const fields = [
      { label: 'Date of Birth', value: formatDate(employee?.personal?.dob) },
      { label: 'Gender', value: formatEnumValue(employee?.personal?.gender) },
      { label: 'Marital Status', value: formatEnumValue(employee?.personal?.maritalStatus) },
    ];

    if (shouldShowSpouse && employee?.personal?.spouseName) {
      fields.push({ label: "Spouse's Name", value: getDisplayValue(employee.personal.spouseName) });
    } else {
      fields.push({ label: "Father's Name", value: getDisplayValue(employee?.personal?.fatherName) });
    }

    fields.push(
      { label: 'Aadhaar Number', value: getDisplayValue(employee?.aadhar) },
      {
        label: 'Same As Current Address',
        value: getBooleanDisplay(employee?.personal?.sameAsCurrentAddress),
      },
    );

    return fields;
  }, [employee]);

  const contactFields = useMemo(
    () => [
      { label: 'Phone Number', value: getDisplayValue(employee?.phone) },
      { label: 'Email Address', value: getDisplayValue(employee?.email) },
      { label: 'Current Address', value: renderAddressValue(employee?.personal?.currentAddress) },
      { label: 'Permanent Address', value: renderAddressValue(employee?.personal?.permanentAddress) },
    ],
    [employee],
  );

  const pfFields = useMemo(
    () => [
      { label: 'UAN Number', value: getDisplayValue(employee?.pf?.uanNumber) },
      { label: 'ESI Number', value: getDisplayValue(employee?.pf?.esiNumber) },
      { label: 'Insurer Name', value: getDisplayValue(employee?.pf?.insurerName) },
    ],
    [employee],
  );

  const bankFields = useMemo(
    () => [
      { label: 'Account Number', value: getDisplayValue(employee?.bank?.accountNumber) },
      { label: 'IFSC Code', value: getDisplayValue(employee?.bank?.ifscCode) },
      { label: 'Bank Name', value: getDisplayValue(employee?.bank?.bankName) },
      { label: 'PAN Number', value: getDisplayValue(employee?.bank?.panCard) },
      { label: 'Driving License', value: getDisplayValue(employee?.bank?.drivingLicense) },
    ],
    [employee],
  );

  const professionalFields = useMemo(
    () => [
      { label: 'Last Company', value: getDisplayValue(employee?.professional?.lastCompany) },
      { label: 'Previous Company Designation', value: getDisplayValue(employee?.professional?.lastDesignation ?? employee?.lastDesignation) },
      { label: 'From Date', value: formatDate(employee?.professional?.fromDate ?? employee?.professional?.experienceFrom) },
      { label: 'To Date', value: formatDate(employee?.professional?.toDate ?? employee?.professional?.experienceTo) },
    ],
    [employee],
  );

  const workingFields = useMemo(() => {
    const workingTime = employee?.workingTime ?? {};
    const shifts = Array.isArray(workingTime.shifts) ? workingTime.shifts : [];
    const normalizedShifts = shifts
      .map((shift, index) => ({
        ...shift,
        displayOrder: Number.isFinite(Number(shift?.displayOrder))
          ? Number(shift.displayOrder)
          : index,
      }))
      .sort((a, b) => a.displayOrder - b.displayOrder || (a.id ?? 0) - (b.id ?? 0));

    const primaryShift =
      normalizedShifts.find((shift) => shift.isPrimary) ?? normalizedShifts[0] ?? null;
    const additionalShifts = primaryShift
      ? normalizedShifts.filter((shift) => shift !== primaryShift)
      : normalizedShifts.slice(1);

    const fields = [
      { label: 'Employment Type', value: formatEnumValue(workingTime.type) },
      {
        label: 'Primary Shift',
        value: primaryShift
          ? formatShiftDisplay(primaryShift)
          : formatShiftDisplay({
              timeFrom: workingTime.timeFrom,
              timeTo: workingTime.timeTo,
              shiftName: null,
            }),
      },
    ];

    if (additionalShifts.length > 0) {
      fields.push({
        label: 'Additional Shifts',
        value: additionalShifts.map((shift) => formatShiftDisplay(shift)).join(', '),
      });
    }

    return fields;
  }, [employee]);

  const qualificationEntries = useMemo(() => {
    const entries = Array.isArray(employee?.education?.qualifications)
      ? employee.education.qualifications
      : [];

    return entries.map((entry, index) => {
      const code = (entry.qualificationType || '').toUpperCase();
      const titleSource =
        qualificationLookup[code] ||
        entry.qualificationTitle ||
        entry.qualificationName ||
        entry.qualificationType ||
        `Qualification ${index + 1}`;

      const normalizedTitle = String(titleSource || '')
        .trim()
        .toUpperCase();

      const resolvedDocumentPath = entry.documentUrl || entry.documentPath || '';
      const documentUrl = resolveDocumentUrl(resolvedDocumentPath);
      const fallbackDocumentName = extractFileNameFromPath(resolvedDocumentPath);

      const fields = [
        {
          label: 'Course / Specialisation',
          value: getDisplayValue(entry.courseName),
        },
        {
          label: 'Institution',
          value: getDisplayValue(entry.institutionName),
        },
        {
          label: 'Year of Passing',
          value: getDisplayValue(entry.yearOfPassing),
        },
        {
          label: 'Grade / Percentage',
          value: getDisplayValue(entry.gradeOrPercentage),
        },
      ];

      const documentLabel = getDisplayValue(entry.fileName);
      const supportingDocumentLabel =
        documentLabel !== '—'
          ? documentLabel
          : getDisplayValue(fallbackDocumentName) !== '—'
            ? fallbackDocumentName
            : documentUrl
              ? extractFileNameFromPath(documentUrl) || 'View Document'
              : '—';
      fields.push({
        label: 'Supporting Document',
        value: supportingDocumentLabel,
        documentUrl,
      });

      return {
        id: entry.id || entry.localId || `qualification-${index}`,
        title: `${index + 1}. ${normalizedTitle || 'QUALIFICATION'}`,
        fields,
      };
    });
  }, [employee?.education?.qualifications, qualificationLookup]);

  const familyMembers = useMemo(
    () => (Array.isArray(employee?.family) ? employee.family : []),
    [employee?.family],
  );

  const documents = useMemo(
    () => (Array.isArray(employee?.documents) ? employee.documents : []),
    [employee?.documents],
  );

  const isInactiveStatus = useMemo(() => {
    const status = (employee?.status || '').toLowerCase();
    return status === 'inactive' || status === 'relieved';
  }, [employee?.status]);

  const relievingDocuments = useMemo(() => {
    if (!isInactiveStatus) {
      return [];
    }

    const historyEntries = Array.isArray(employee?.serviceHistory)
      ? employee.serviceHistory
      : [];

    return historyEntries
      .filter((entry) => entry?.documentPath)
      .map((entry, index) => {
        const documentPath = entry.documentPath;
        const documentUrl = resolveDocumentUrl(documentPath);
        const rawLabel = entry.documentTypeName || entry.documentType || 'Relieving Document';
        const documentType = formatEnumValue(rawLabel);
        const fileNameSource =
          entry.fileName ||
          extractFileNameFromPath(documentPath) ||
          documentUrl ||
          'Relieving Document';

        return {
          key: entry.id ?? `relieving-document-${index}`,
          documentType: documentType === '—' ? 'Relieving Document' : documentType,
          fileName: getDisplayValue(fileNameSource),
          url: documentUrl,
          rawPath: documentPath,
        };
      });
  }, [employee?.serviceHistory, isInactiveStatus]);

  const documentItems = useMemo(
    () => {
      const baseDocuments = documents.map((document, index) => ({
        key: document.id ?? `${document.documentType ?? 'document'}-${index}`,
        documentType: formatEnumValue(document.documentType),
        fileName: getDisplayValue(document.fileName),
        url: resolveDocumentUrl(document.fileUrl, document.filePath, document.storedFileKey),
        rawPath: document.fileUrl || document.filePath || document.storedFileKey,
      }));

      const seen = new Set(
        baseDocuments
          .map((document) => document.url || document.rawPath || document.fileName)
          .filter(Boolean),
      );

      const mergedRelieving = relievingDocuments.filter((document) => {
        const identity = document.url || document.rawPath || document.fileName;
        if (!identity) {
          return true;
        }
        if (seen.has(identity)) {
          return false;
        }
        seen.add(identity);
        return true;
      });

      return [...baseDocuments, ...mergedRelieving];
    },
    [documents, relievingDocuments],
  );

  const metadataFields = useMemo(
    () => [
      {
        label: 'Approved By',
        value: buildUserTimestampValue(
          employee?.approvedBy || employee?.approvedByName || employee?.approvedById,
          employee?.approvedAt,
        ),
      },
      {
        label: 'Last Updated By',
        value: buildUserTimestampValue(
          employee?.updatedBy || employee?.updatedByName,
          employee?.updatedAt,
        ),
      },
      { label: 'Created On', value: formatDateTime(employee?.createdAt) },
      { label: 'Last Updated On', value: formatDateTime(employee?.updatedAt) },
    ],
    [
      employee?.approvedAt,
      employee?.approvedBy,
      employee?.approvedByName,
      employee?.createdAt,
      employee?.updatedAt,
      employee?.updatedBy,
      employee?.updatedByName,
    ],
  );

  const statusDetails = useMemo(() => {
    if ((employee?.status || '').toLowerCase() !== 'inactive') {
      return [];
    }

    const details = [
      { label: 'Relieving Date', value: formatDate(employee?.relievingDate) },
      { label: 'Relieving Reason', value: formatEnumValue(employee?.inactiveReason) },
    ];

    if (employee?.inactiveNotes) {
      details.push({
        label: 'Notes',
        value: renderAddressValue(employee.inactiveNotes),
      });
    }

    return details.filter((detail) => detail.value && detail.value !== '—');
  }, [employee?.status, employee?.relievingDate, employee?.inactiveReason, employee?.inactiveNotes]);

  const pfHistoryEntries = useMemo(() => {
    if (!Array.isArray(employee?.pfHistory) || employee.pfHistory.length === 0) {
      return [];
    }

    return employee.pfHistory.map((entry, index) => ({
      id: entry.id ?? `pf-history-${index}`,
      effectiveDate: entry.effectiveDate ? formatDate(entry.effectiveDate) : '—',
      uanNumber: getDisplayValue(entry.uanNumber),
      esiNumber: getDisplayValue(entry.esiNumber),
      insurerName: getDisplayValue(entry.insurerName),
    }));
  }, [employee?.pfHistory]);

  const serviceHistoryEntries = useMemo(() => {
    const periods = Array.isArray(serviceHistoryTimeline.periods)
      ? serviceHistoryTimeline.periods
      : [];

    if (periods.length === 0) {
      return [];
    }

    return periods.map((period, index) => ({
      id: period.id ?? `service-history-${index}`,
      status: formatEnumValue(period.status),
      reason: period.reasonLabel ? renderAddressValue(period.reasonLabel) : '—',
      notes: period.notesLabel ? renderAddressValue(period.notesLabel) : '—',
      outlet: period.outlet?.name ? getDisplayValue(period.outlet.name) : '—',
      designation: getDisplayValue(period.designation ?? period.currentDesignation ?? employee?.designation),
      timeline: period.timelineLabel,
    }));
  }, [employee?.designation, serviceHistoryTimeline.periods]);

  const professionalHasData = professionalFields.some((field) => field.value !== '—');
  const workingHasData = workingFields.some((field) => field.value !== '—');
  const hasEducation = qualificationEntries.length > 0;
  const hasFamilyMembers = familyMembers.length > 0;
  const hasDocuments = documentItems.length > 0;

  return (
    <DashboardLayout title="Employee Details">
      {documentPreview.visible && documentPreview.url && (
        <div
          className="employee-view__preview-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="employee-document-preview-title"
          onClick={handleCloseDocumentPreview}
        >
          <div
            className="employee-view__preview-container"
            role="document"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="employee-view__preview-header">
              <h2 id="employee-document-preview-title">{documentPreview.title || 'Document Preview'}</h2>
              <button
                type="button"
                className="employee-view__preview-close"
                onClick={handleCloseDocumentPreview}
                aria-label="Close document preview"
              >
                ×
              </button>
            </div>
            <iframe
              title={documentPreview.title || 'Document preview'}
              src={documentPreview.url}
              className="employee-view__preview-frame"
            />
            <div className="employee-view__preview-footer">
              <button
                type="button"
                className="employee-view__preview-close-btn"
                onClick={handleCloseDocumentPreview}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="employee-view__header">
        <button type="button" className="employee-view__back" onClick={() => navigate(-1)}>
          <FaArrowLeft aria-hidden="true" />
          <span>Back to Employees</span>
        </button>
        <div className="employee-view__actions">
          {canExportEmployee && (
            <>
              <button
                type="button"
                className="employee-view__action"
                onClick={handleForm11Preview}
                disabled={!employee?.id || form11Loading}
                aria-busy={form11Loading ? 'true' : undefined}
              >
                {form11Loading ? 'Preparing Form 11…' : 'Preview Form 11'}
              </button>
              <button
                type="button"
                className="employee-view__action"
                onClick={handleFormUPreview}
                disabled={!employee?.id || formUPreviewLoading}
                aria-busy={formUPreviewLoading ? 'true' : undefined}
              >
                {formUPreviewLoading ? 'Preparing Form U…' : 'Preview Form U'}
              </button>
              <button
                type="button"
                className="employee-view__action"
                onClick={handleGsmPdfPreview}
                disabled={!employee?.id || gsmPreviewLoading}
                aria-busy={gsmPreviewLoading ? 'true' : undefined}
              >
                {gsmPreviewLoading ? 'Preparing Resume…' : 'Preview Resume'}
              </button>
            </>
          )}
          {canEditEmployee && (
            employee?.id ? (
              <Link
                to={`/add-employee?employeeId=${employee.id}`}
                className="employee-view__cta"
              >
                Edit Employee
              </Link>
            ) : (
              <span className="employee-view__cta employee-view__cta--disabled" aria-disabled="true">
                Edit Employee
              </span>
            )
          )}
        </div>
      </div>

      {form11Error && (
        <div className="alert alert-warning" role="alert">
          {form11Error}
        </div>
      )}

      {formUError && (
        <div className="alert alert-warning" role="alert">
          {formUError}
        </div>
      )}

      {gsmError && (
        <div className="alert alert-warning" role="alert">
          {gsmError}
        </div>
      )}

      {loading && (
        <div className="employee-view__loader" role="status" aria-live="polite">
          <div className="spinner-border text-danger" role="presentation" />
          <p>Loading employee information...</p>
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && employee && (
        <div className="employee-view">
          <section className="employee-view__summary-card">
            <div className="employee-view__summary-heading">
              <div className="employee-view__summary-primary">
                <div
                  className="employee-view__avatar"
                  aria-label={
                    profilePhoto?.url
                      ? undefined
                      : employee?.name
                      ? `Avatar for ${employee.name}`
                      : 'Employee avatar placeholder'
                  }
                >
                  {profilePhoto?.url ? (
                    <img
                      src={profilePhoto.url}
                      alt={profilePhoto.altText || 'Employee photo'}
                    />
                  ) : (
                    <span aria-hidden="true">{profileInitial || 'NA'}</span>
                  )}
                </div>
                <div>
                  <h2>{getDisplayValue(employee.name)}</h2>
                  <p>{designationValue === '—' ? 'Designation not available' : designationValue}</p>
                </div>
              </div>
              <span className={`employee-view__status employee-view__status--${statusLabel.tone}`}>
                {statusLabel.label}
              </span>
            </div>
            <div className="employee-view__summary-grid">
              {summaryFields.map((field) => (
                <div key={field.label}>
                  <span className="employee-view__summary-label">{field.label}</span>
                  <span className="employee-view__summary-value">{field.value}</span>
                </div>
              ))}
            </div>
            <div className="employee-view__login-card" aria-live="polite">
              <div className="employee-view__login-header">
                <div>
                  <h3>Mobile Login Access</h3>
                  <p>Control the employee’s mobile login credentials.</p>
                </div>
                {canManageMobileLogin && !isEditingLogin && (
                  <button
                    type="button"
                    className="employee-view__login-trigger"
                    onClick={handleOpenLoginEditor}
                    disabled={loginLoading}
                  >
                    {loginDetails ? 'Edit Login' : 'Create Login'}
                  </button>
                )}
              </div>

              {loginLoading ? (
                <p className="employee-view__login-note">Loading login details…</p>
              ) : (
                <>
                  {loginError && (
                    <div className="employee-view__login-alert employee-view__login-alert--error" role="alert">
                      {loginError}
                    </div>
                  )}
                  {loginSuccess && (
                    <div className="employee-view__login-alert employee-view__login-alert--success" role="status">
                      {loginSuccess}
                    </div>
                  )}
                  {!isEditingLogin && loginInfoMessage && (
                    <p className="employee-view__login-note">{loginInfoMessage}</p>
                  )}
                  {!isEditingLogin && loginDetails && (
                    <dl className="employee-view__login-details">
                      <div>
                        <dt>Username</dt>
                        <dd>{getDisplayValue(loginDetails.username)}</dd>
                      </div>
                      <div>
                        <dt>Shared Password</dt>
                        <dd>
                          {loginDetails.temporaryPassword
                            ? loginDetails.temporaryPassword
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Status</dt>
                        <dd>{formatEnumValue(loginDetails.status)}</dd>
                      </div>
                      <div>
                        <dt>Last Login</dt>
                        <dd>{formatDateTime(loginDetails.lastLoginAt)}</dd>
                      </div>
                      <div>
                        <dt>Password Updated</dt>
                        <dd>{formatDateTime(loginDetails.passwordChangedAt)}</dd>
                      </div>
                    </dl>
                  )}
                  {isEditingLogin && (
                    <form className="employee-view__login-form" onSubmit={handleLoginSubmit}>
                      <label className="employee-view__login-field">
                        <span>Username</span>
                        <input
                          type="text"
                          className="employee-view__login-input"
                          value={loginForm.username}
                          onChange={handleLoginInputChange('username')}
                          autoComplete="username"
                          required
                        />
                      </label>
                      <label className="employee-view__login-field">
                        <span>{loginDetails ? 'New Password' : 'Password'}</span>
                        <input
                          type="password"
                          className="employee-view__login-input"
                          value={loginForm.password}
                          onChange={handleLoginInputChange('password')}
                          autoComplete="new-password"
                          placeholder={loginDetails ? 'Leave blank to keep existing password' : 'Enter a password'}
                        />
                      </label>
                      <label className="employee-view__login-field">
                        <span>Status</span>
                        <select
                          className="employee-view__login-input"
                          value={loginForm.status}
                          onChange={handleLoginInputChange('status')}
                        >
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </label>
                      <div className="employee-view__login-actions">
                        <button
                          type="button"
                          className="employee-view__login-cancel"
                          onClick={handleCancelLoginEdit}
                          disabled={loginSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="employee-view__login-submit"
                          disabled={loginSaving}
                        >
                          {loginSaving
                            ? 'Saving…'
                            : loginDetails
                              ? 'Update Login'
                              : 'Create Login'}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}

              {!isEditingLogin && (
                <div className="employee-view__login-footer">
                  <span>Mobile login link</span>
                  <a href="/employee/mobile/login" target="_blank" rel="noopener noreferrer">
                    Open page
                  </a>
                </div>
              )}
            </div>
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaIdCardAlt />
              </span>
              <h3>Personal Information</h3>
            </header>
            <div className="employee-view__fields">
              {personalFields.map((field) => (
                <div key={field.label} className="employee-view__field">
                  <span className="employee-view__field-label">{field.label}</span>
                  <span className="employee-view__field-value">{field.value}</span>
                </div>
              ))}
            </div>
          </section>

          {statusDetails.length > 0 && (
            <section className="employee-view__section">
              <header className="employee-view__section-header">
                <span className="employee-view__section-icon" aria-hidden="true">
                  <FaBriefcase />
                </span>
                <h3>Employment Status Details</h3>
              </header>
              <div className="employee-view__fields">
                {statusDetails.map((detail) => (
                  <div key={detail.label} className="employee-view__field">
                    <span className="employee-view__field-label">{detail.label}</span>
                    <span className="employee-view__field-value">{detail.value}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaPhoneAlt />
              </span>
              <h3>Contact Information</h3>
            </header>
            <div className="employee-view__fields">
              {contactFields.map((field) => (
                <div key={field.label} className="employee-view__field">
                  <span className="employee-view__field-label">{field.label}</span>
                  <span className="employee-view__field-value">{field.value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaShieldAlt />
              </span>
              <h3>PF Information</h3>
            </header>
            <div className="employee-view__fields">
              {pfFields.map((field) => (
                <div key={field.label} className="employee-view__field">
                  <span className="employee-view__field-label">{field.label}</span>
                  <span className="employee-view__field-value">{field.value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaFileAlt />
              </span>
              <h3>PF History</h3>
            </header>
            {pfHistoryEntries.length === 0 ? (
              <p className="employee-view__empty">No PF history recorded.</p>
            ) : (
              <div className="employee-view__table">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Effective Date</th>
                      <th scope="col">UAN Number</th>
                      <th scope="col">ESI Number</th>
                      <th scope="col">Insurer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pfHistoryEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td data-label="Effective Date">{entry.effectiveDate}</td>
                        <td data-label="UAN Number">{entry.uanNumber}</td>
                        <td data-label="ESI Number">{entry.esiNumber}</td>
                        <td data-label="Insurer">{entry.insurerName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaHistory />
              </span>
              <h3>Service History</h3>
            </header>
            {serviceHistoryEntries.length === 0 ? (
              <p className="employee-view__empty">No service history recorded.</p>
            ) : (
              <div className="employee-view__table">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Status</th>
                      <th scope="col">Timeline</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Notes</th>
                      <th scope="col">Outlet</th>
                      <th scope="col">Designation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceHistoryEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td data-label="Status">{entry.status}</td>
                        <td data-label="Timeline">{entry.timeline}</td>
                        <td data-label="Reason">{entry.reason}</td>
                        <td data-label="Notes">{entry.notes}</td>
                        <td data-label="Outlet">{entry.outlet}</td>
                        <td data-label="Designation">{entry.designation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaWallet />
              </span>
              <h3>Bank Information</h3>
            </header>
            <div className="employee-view__fields">
              {bankFields.map((field) => (
                <div key={field.label} className="employee-view__field">
                  <span className="employee-view__field-label">{field.label}</span>
                  <span className="employee-view__field-value">{field.value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaBriefcase />
              </span>
              <h3>Employment History</h3>
            </header>
            {professionalHasData ? (
              <div className="employee-view__fields">
                {professionalFields.map((field) => (
                  <div key={field.label} className="employee-view__field">
                    <span className="employee-view__field-label">{field.label}</span>
                    <span className="employee-view__field-value">{field.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="employee-view__empty">No employment history recorded.</p>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaRegClock />
              </span>
              <h3>Work Schedule</h3>
            </header>
            {workingHasData ? (
              <div className="employee-view__fields">
                {workingFields.map((field) => (
                  <div key={field.label} className="employee-view__field">
                    <span className="employee-view__field-label">{field.label}</span>
                    <span className="employee-view__field-value">{field.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="employee-view__empty">No work schedule information available.</p>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaGraduationCap />
              </span>
              <h3>Education Details</h3>
            </header>
            {qualificationLookupError && (
              <p className="employee-view__note" role="status">{qualificationLookupError}</p>
            )}
            {hasEducation ? (
              <div className="employee-view__education">
                {qualificationEntries.map((entry) => (
                  <div key={entry.id} className="employee-view__education-card">
                    <h4>{entry.title}</h4>
                    <div className="employee-view__fields">
                      {entry.fields.map((field) => (
                        <div key={field.label} className="employee-view__field">
                          <span className="employee-view__field-label">{field.label}</span>
                          {field.documentUrl && field.value !== '—' ? (
                            <a
                              href={field.documentUrl}
                              className="employee-view__field-value"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {field.value}
                            </a>
                          ) : (
                            <span className="employee-view__field-value">{field.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="employee-view__empty">No education details provided.</p>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaUsers />
              </span>
              <h3>Family Members</h3>
            </header>
            {hasFamilyMembers ? (
              <div className="employee-view__list">
                {familyMembers.map((member) => (
                  <div key={member.id || `${member.name}-${member.relation}`} className="employee-view__list-item">
                    <div className="employee-view__list-main">
                      <p className="employee-view__list-title">{getDisplayValue(member.name)}</p>
                      <span className="employee-view__chip">{formatEnumValue(member.relation)}</span>
                    </div>
                    <div className="employee-view__list-meta">
                      <span>
                        <FaPhoneAlt aria-hidden="true" />
                        {` ${getDisplayValue(member.mobile)}`}
                      </span>
                      <span>
                        <FaCalendarAlt aria-hidden="true" />
                        {` ${formatDate(member.dob)}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="employee-view__empty">No family members recorded.</p>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaFolderOpen />
              </span>
              <h3>Documents</h3>
              {canAddEmployeeDocument ? (
                <button
                  type="button"
                  className="employee-view__add-document-btn"
                  onClick={handleOpenAddDocument}
                >
                  + Add Document
                </button>
              ) : null}
            </header>
            {documentUploadMessage ? (
              <p className="employee-view__success">{documentUploadMessage}</p>
            ) : null}
            {isAddDocumentOpen ? (
              <form className="employee-view__add-document-card" onSubmit={handleAddDocumentSubmit}>
                <label>
                  <span>Document Type</span>
                  <select
                    value={documentUploadForm.documentTypeId}
                    onChange={(event) => setDocumentUploadForm((prev) => ({ ...prev, documentTypeId: event.target.value }))}
                    disabled={documentUploadLoading || isLoadingDocumentTypes}
                  >
                    <option value="">Select Document Type</option>
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>File</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(event) => setDocumentUploadForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
                    disabled={documentUploadLoading}
                  />
                </label>
                {documentTypeError ? <p className="employee-view__error-text">{documentTypeError}</p> : null}
                {documentUploadError ? <p className="employee-view__error-text">{documentUploadError}</p> : null}
                <div className="employee-view__add-document-actions">
                  <button type="submit" disabled={documentUploadLoading}>
                    {documentUploadLoading ? 'Uploading...' : 'Upload'}
                  </button>
                  <button type="button" onClick={handleCloseAddDocument} disabled={documentUploadLoading}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
            {hasDocuments ? (
              <div className="employee-view__documents">
                <table className="employee-view__documents-table">
                  <thead>
                    <tr>
                      <th scope="col">Document Type</th>
                      <th scope="col">File Name</th>
                      <th scope="col" className="employee-view__documents-action-heading">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentItems.map((document) => (
                      <tr key={document.key}>
                        <td data-label="Document Type">{document.documentType}</td>
                        <td data-label="File Name">{document.fileName}</td>
                        <td data-label="Action">
                          {document.url ? (
                            <a
                              href={document.url}
                              className="employee-view__document-action"
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View document"
                            >
                              <FaEye aria-hidden="true" />
                              <span>View</span>
                            </a>
                          ) : (
                            <span className="employee-view__document-placeholder">No file uploaded</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="employee-view__empty">No documents uploaded.</p>
            )}
          </section>

          <section className="employee-view__section">
            <header className="employee-view__section-header">
              <span className="employee-view__section-icon" aria-hidden="true">
                <FaInfoCircle />
              </span>
              <h3>Record Metadata</h3>
            </header>
            <div className="employee-view__meta">
              {metadataFields.map((field) => (
                <div key={field.label} className="employee-view__field">
                  <span className="employee-view__field-label">{field.label}</span>
                  <span className="employee-view__field-value">{field.value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EmployeeView;
